// See NotificationBar.spec.md for NB-FR-004

import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { DEFAULT_DRAWER_HEIGHT, HEADER_HEIGHT, ROW_HEIGHT } from './NotificationBarDrawer'

// Smallest open drawer: the header row and one event row
export const MIN_DRAWER_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT

export interface NotificationBarLayout {
  open: boolean
  height: number
}

const ARROW_STEPS: Record<string, number> = { ArrowUp: ROW_HEIGHT, ArrowDown: -ROW_HEIGHT }

// NB-FR-004: Open state and height of the drawer. Nothing is saved: every page load starts with
// a collapsed bar (product decision). `getMaxHeight` returns the height available above the bar.
export default function useDrawerLayout(getMaxHeight: () => number) {
  const getMax = useRef(getMaxHeight)
  getMax.current = getMaxHeight
  const readMaxHeight = useCallback(() => Math.max(getMax.current(), MIN_DRAWER_HEIGHT), [])
  const clamp = useCallback(
    (height: number) => Math.round(Math.min(Math.max(height, MIN_DRAWER_HEIGHT), readMaxHeight())),
    [readMaxHeight],
  )
  // Fitted to the window, like every other height the drawer takes
  const [layout, setLayout] = useState<NotificationBarLayout>(() => ({
    open: false,
    height: clamp(DEFAULT_DRAWER_HEIGHT),
  }))
  // Kept in state so the handle's aria-valuemax follows the window even when the height doesn't
  // change
  const [maxHeight, setMaxHeight] = useState(readMaxHeight)

  // NB-FR-004: A smaller window keeps the drawer within the available height
  useEffect(() => {
    const onResize = () => {
      setMaxHeight(readMaxHeight())
      setLayout((current) => {
        const height = clamp(current.height)
        return height === current.height ? current : { ...current, height }
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clamp, readMaxHeight])

  // Removes the window listeners of a drag in progress
  const endDrag = useRef<(() => void) | null>(null)
  useEffect(() => () => endDrag.current?.(), [])

  // NB-FR-004: The toggle always opens at the default 5-row height
  const open = useCallback(
    () => setLayout({ open: true, height: clamp(DEFAULT_DRAWER_HEIGHT) }),
    [clamp],
  )
  const close = useCallback(() => setLayout((current) => ({ ...current, open: false })), [])

  // NB-FR-004: Dragging the bar's top edge resizes the drawer; dragging it up from a collapsed bar
  // opens it, and dragging it down to the bar collapses it. A cancelled pointer (touch interrupted
  // by the browser) ends the drag like a release.
  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      event.preventDefault()
      endDrag.current?.()
      const startY = event.clientY
      const startHeight = layout.open ? layout.height : 0
      let collapse = false
      const onMove = (moveEvent: globalThis.PointerEvent) => {
        const raw = startHeight + (startY - moveEvent.clientY)
        collapse = raw < MIN_DRAWER_HEIGHT
        setLayout((current) =>
          collapse ? { ...current, height: clamp(raw) } : { open: true, height: clamp(raw) },
        )
      }
      const removeListeners = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onEnd)
        window.removeEventListener('pointercancel', onEnd)
        endDrag.current = null
      }
      const onEnd = () => {
        removeListeners()
        if (collapse) setLayout((current) => ({ ...current, open: false }))
      }
      endDrag.current = removeListeners
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onEnd)
      window.addEventListener('pointercancel', onEnd)
    },
    [layout.open, layout.height, clamp],
  )

  // NB-FR-004: Arrow Up/Down on the focused handle resize the drawer one row at a time; Arrow Up
  // on a collapsed bar opens it like the toggle
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const step = ARROW_STEPS[event.key]
      if (!step) return
      event.preventDefault()
      setLayout((current) => {
        if (!current.open) {
          return step > 0 ? { open: true, height: clamp(DEFAULT_DRAWER_HEIGHT) } : current
        }
        return { ...current, height: clamp(current.height + step) }
      })
    },
    [clamp],
  )

  return {
    open: layout.open,
    height: layout.height,
    maxHeight,
    openDrawer: open,
    closeDrawer: close,
    handleProps: { onPointerDown, onKeyDown },
  }
}
